/**
 * Timezone-correct date helpers.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The previous helpers used `new Date().toISOString().split('T')[0]`, which is
 * the **UTC** date. For a tenant in Asia/Kolkata that means "Today" began at
 * 05:30 local; for a US Pacific tenant it rolls over at 17:00 local. Every
 * default date range in the app was therefore wrong for most of the world, and
 * the numbers on screen never matched the day the user had in mind.
 *
 * THE CONTRACT
 * ------------
 * - A "business day" is a calendar date in the tenant's configured timezone.
 * - The frontend works in local calendar dates (YYYY-MM-DD) and always sends
 *   the IANA timezone name alongside them.
 * - The backend converts (date, tz) -> UTC instants using pytz, because JS
 *   cannot be trusted with historical DST transitions and Python already can.
 *
 * Nothing here creates a Date from a bare YYYY-MM-DD string without an explicit
 * time part: `new Date('2026-08-19')` is parsed as UTC midnight, which is the
 * same class of bug this file exists to remove.
 */

/** The browser's own timezone — the default until the tenant configures one. */
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** True if `tz` is a timezone this runtime can actually resolve. */
export const isValidTimeZone = (tz) => {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const partsIn = (date, tz) => {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const out = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') out[p.type] = p.value;
  }
  // Intl renders midnight as hour "24" in some engines.
  if (out.hour === '24') out.hour = '00';
  return out;
};

/** Today's calendar date in `tz`, as YYYY-MM-DD. */
export const todayIn = (tz, now = new Date()) => {
  const p = partsIn(now, tz);
  return `${p.year}-${p.month}-${p.day}`;
};

/** Current wall-clock time in `tz`, as HH:MM. */
export const clockIn = (tz, now = new Date()) => {
  const p = partsIn(now, tz);
  return `${p.hour}:${p.minute}`;
};

/** Parse YYYY-MM-DD into its numeric parts. Never touches Date. */
const ymd = (dateStr) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || ''));
  if (!m) return null;
  return { y: +m[1], m: +m[2], d: +m[3] };
};

/** Days between two YYYY-MM-DD dates (b - a), calendar-exact. */
export const daysBetween = (a, b) => {
  const pa = ymd(a);
  const pb = ymd(b);
  if (!pa || !pb) return 0;
  const ua = Date.UTC(pa.y, pa.m - 1, pa.d);
  const ub = Date.UTC(pb.y, pb.m - 1, pb.d);
  return Math.round((ub - ua) / 86400000);
};

/** Shift a YYYY-MM-DD by `n` calendar days. DST-safe: pure calendar maths. */
export const shiftDate = (dateStr, n) => {
  const p = ymd(dateStr);
  if (!p) return dateStr;
  const t = new Date(Date.UTC(p.y, p.m - 1, p.d));
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};

/** `n` days before today in `tz`. */
export const daysAgoIn = (tz, n, now = new Date()) => shiftDate(todayIn(tz, now), -n);

/** Weekday index for a YYYY-MM-DD, 0 = Monday. */
export const weekdayIndex = (dateStr) => {
  const p = ymd(dateStr);
  if (!p) return 0;
  const jsDay = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay(); // 0 = Sunday
  return (jsDay + 6) % 7;
};

/** Monday of the week containing today in `tz`. */
export const weekStartIn = (tz, now = new Date()) => {
  const today = todayIn(tz, now);
  return shiftDate(today, -weekdayIndex(today));
};

/** First day of the current month in `tz`. */
export const monthStartIn = (tz, now = new Date()) => todayIn(tz, now).slice(0, 8) + '01';

/* ─── Date range presets ─────────────────────────────────────────────────── */

export const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7days', label: 'Last 7 days' },
  { id: 'last30days', label: 'Last 30 days' },
  { id: 'thisweek', label: 'This week' },
  { id: 'thismonth', label: 'This month' },
  { id: 'last90days', label: 'Last 90 days' },
  { id: 'custom', label: 'Custom range' },
];

/**
 * Resolve a preset id into concrete local calendar dates.
 * Returns { dateRange, startDate, endDate }.
 */
export const resolvePreset = (preset, tz, now = new Date()) => {
  const today = todayIn(tz, now);
  switch (preset) {
    case 'today':
      return { dateRange: 'today', startDate: today, endDate: today };
    case 'yesterday': {
      const y = shiftDate(today, -1);
      return { dateRange: 'yesterday', startDate: y, endDate: y };
    }
    case 'last7days':
      return { dateRange: 'last7days', startDate: shiftDate(today, -6), endDate: today };
    case 'last30days':
      return { dateRange: 'last30days', startDate: shiftDate(today, -29), endDate: today };
    case 'last90days':
      return { dateRange: 'last90days', startDate: shiftDate(today, -89), endDate: today };
    case 'thisweek':
      return { dateRange: 'thisweek', startDate: weekStartIn(tz, now), endDate: today };
    case 'thismonth':
      return { dateRange: 'thismonth', startDate: monthStartIn(tz, now), endDate: today };
    default:
      return { dateRange: 'last7days', startDate: shiftDate(today, -6), endDate: today };
  }
};

/**
 * The comparison window for a period, matching the backend exactly:
 *   span      = (end - start) + 1 day
 *   prev_end  = start - 1 day
 *   prev_start= prev_end - (span - 1)
 *
 * Single-day ranges compare to the SAME WEEKDAY LAST WEEK, not to yesterday —
 * a Monday-vs-Sunday delta on call volume is noise, and single-day is the range
 * users pick most often.
 */
export const comparisonWindow = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const span = daysBetween(startDate, endDate) + 1;
  if (span <= 0) return null;
  if (span === 1) {
    const d = shiftDate(startDate, -7);
    return { startDate: d, endDate: d, mode: 'same_weekday_last_week', span: 1 };
  }
  const prevEnd = shiftDate(startDate, -1);
  const prevStart = shiftDate(prevEnd, -(span - 1));
  return { startDate: prevStart, endDate: prevEnd, mode: 'preceding_period', span };
};

/* ─── Labels ─────────────────────────────────────────────────────────────── */


/** "19 Aug" or "19 Aug 2025" when the year differs from the current one. */
export const formatDay = (dateStr, tz) => {
  const p = ymd(dateStr);
  if (!p) return dateStr || '';
  const thisYear = +todayIn(tz || 'UTC').slice(0, 4);
  const base = `${p.d} ${MONTHS[p.m - 1]}`;
  return p.y === thisYear ? base : `${base} ${p.y}`;
};

/** "19–25 Aug" · "28 Jul – 3 Aug" · "19 Aug" for a single day. */
export const formatRange = (startDate, endDate, tz) => {
  const a = ymd(startDate);
  const b = ymd(endDate);
  if (!a || !b) return '';
  if (startDate === endDate) return formatDay(startDate, tz);
  if (a.y === b.y && a.m === b.m) return `${a.d}–${b.d} ${MONTHS[b.m - 1]}`;
  return `${formatDay(startDate, tz)} – ${formatDay(endDate, tz)}`;
};

/** Human label for a filter object. Always concrete — never a bare preset name. */
export const rangeLabel = (filters, tz) => {
  if (!filters) return '';
  const preset = DATE_PRESETS.find((p) => p.id === filters.dateRange);
  if (filters.dateRange === 'custom' || !preset) {
    return formatRange(filters.startDate, filters.endDate, tz) || 'Custom range';
  }
  return preset.label;
};

/* ─── Rendering an instant in the business timezone ──────────────────────── */

/**
 * Format a stored ISO instant in the tenant's timezone.
 * The old helpers were named formatIST* but actually rendered in the *browser's*
 * timezone, so a UK manager reviewing an Indian team's calls saw UK times against
 * IST-bucketed analytics.
 */
export const formatInstant = (iso, tz, { withDate = true, withYear = false, hour12 = true } = {}) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return String(iso);
  try {
    const p = partsIn(d, tz);
    const h24 = Number(p.hour);
    const time = hour12
      ? `${h24 % 12 === 0 ? 12 : h24 % 12}:${p.minute} ${h24 < 12 ? 'am' : 'pm'}`
      : `${p.hour}:${p.minute}`;
    if (!withDate) return time;
    const day = `${Number(p.day)} ${MONTHS[Number(p.month) - 1]}`;
    return withYear ? `${day} ${p.year}, ${time}` : `${day}, ${time}`;
  } catch {
    return String(iso);
  }
};

export const formatTimeOnly = (iso, tz, { hour12 = true } = {}) =>
  formatInstant(iso, tz, { withDate: false, hour12 });

/** "09:00 – 10:00" for an hour index 0-23, already in business-local hours. */
export const formatHourRange = (hour, { hour12 = true } = {}) => {
  const h = Number(hour);
  if (!Number.isFinite(h)) return '';
  const fmt = (x) => {
    if (!hour12) return `${String(x).padStart(2, '0')}:00`;
    const suffix = x < 12 ? 'am' : 'pm';
    const hh = x % 12 === 0 ? 12 : x % 12;
    return `${hh}${suffix}`;
  };
  return `${fmt(h)}–${fmt((h + 1) % 24)}`;
};

/** Relative age of an instant: "4h 12m", "3d 04h", "just now". */
export const formatAge = (iso, now = new Date()) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.max(0, Math.floor((now.getTime() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${String(mins % 60).padStart(2, '0')}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${String(hrs % 24).padStart(2, '0')}h`;
};

/** Minutes since an instant, or null. Used for SLA thresholds. */
export const minutesSince = (iso, now = new Date()) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  return Math.max(0, Math.floor((now.getTime() - then) / 60000));
};

/** A grouped, searchable list of IANA zones for the timezone picker. */
export const TIMEZONE_OPTIONS = (() => {
  const common = [
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo',
    'Asia/Manila', 'Asia/Jakarta', 'Asia/Karachi', 'Asia/Dhaka', 'Asia/Colombo',
    'Asia/Riyadh', 'Asia/Jerusalem',
    'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris',
    'Europe/Berlin', 'Europe/Amsterdam', 'Europe/Zurich', 'Europe/Rome', 'Europe/Warsaw',
    'Europe/Athens', 'Europe/Istanbul', 'Europe/Moscow',
    'America/New_York', 'America/Toronto', 'America/Chicago', 'America/Denver',
    'America/Phoenix', 'America/Los_Angeles', 'America/Vancouver', 'America/Mexico_City',
    'America/Bogota', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
    'Africa/Lagos', 'Africa/Cairo', 'Africa/Nairobi', 'Africa/Johannesburg',
    'Australia/Perth', 'Australia/Brisbane', 'Australia/Sydney', 'Australia/Melbourne',
    'Pacific/Auckland',
    'UTC',
  ];
  let all = common;
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      const supported = Intl.supportedValuesOf('timeZone');
      const extra = supported.filter((z) => !common.includes(z));
      all = [...common, ...extra];
    }
  } catch {
    /* Intl.supportedValuesOf is not available everywhere — the common list stands. */
  }
  return all.filter(isValidTimeZone);
})();

/** "Asia/Kolkata (GMT+05:30)" for the picker. */
export const timeZoneLabel = (tz, now = new Date()) => {
  if (!isValidTimeZone(tz)) return tz || '';
  try {
    const name = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value;
    return name ? `${tz.replace(/_/g, ' ')} (${name})` : tz.replace(/_/g, ' ');
  } catch {
    return tz.replace(/_/g, ' ');
  }
};
